import React from 'react'
import SortableTable, {ColumnConfig} from '../SortableTable'
import {useSelector} from 'react-redux'
import {selectGroupsLookupByActiveOrganization} from '../../reducers/selectors'
import HighlightText from '../HighlightText'
import PropTypes from 'prop-types'
import {getBuildingLabelsColumnConfig} from './BuildingLabelsColumnConfig'
import {getTagLabelsColumnConfig} from './TagLabelsColumnConfig'
import {getResidentGroupsLabel} from '../../Utils/globalAdminUtils'
import EmptyStateIconAndMessage from '../EmptyStateIconAndMessage'
import RXRIcon from '../RXRIcon'

function ListDocumentsTable(props) {
  const documentsLookup = useSelector(state => state.GlobalAdmin.documentsLookup)
  const activeOrganizationId = useSelector(state => state.GlobalAdmin.activeOrganizationId)

  // here we construct a mapping of documentId -> [buildingNames, ...]
  const buildingsLookup = useSelector(state => state.GlobalAdmin.buildingsLookup)
  const documentsByBuilding = useSelector(state => state.GlobalAdmin.documentsByBuilding)
  const buildingsByDocument = Object.entries(documentsByBuilding).reduce((acc, [buildingId, docs]) => {
    docs.forEach(docId => {
      if (!acc[docId]) {
        acc[docId] = []
      }
      acc[docId].push(buildingsLookup[buildingId])
    })
    return acc
  }, {})

  // we use the organization groups to assist in rendering resident group labels
  const orgGroupsLookup = useSelector(selectGroupsLookupByActiveOrganization)

  const lowerFilter = props.filterTerm ? props.filterTerm.toLowerCase() : ''
  const allDocumentsInOrg = Object.values(documentsLookup).filter(d => d.organizationId === activeOrganizationId)
  const hasFiltersApplied = !!(props.filterTerm || props.selectedTagFilters)

  const dataToRender = allDocumentsInOrg
    .map(d => {
      const tags = d.documentTags || []
      return {
        documentId: d.id,
        documentNameInternal: d.internalName || '--', // default to displayName if internalName is not set
        documentNameExternal: d.displayName || '--',
        residentGroups: getResidentGroupsLabel(d.residentGroups, orgGroupsLookup),
        tags: tags,
        tagsLabel: tags.join(', '),
        buildings: buildingsByDocument[d.id] || [],
        buildingsLabel: (buildingsByDocument[d.id] || [])
          .filter(Boolean)
          .map(b => b.displayName)
          .join(', '),
      }
    })
    .filter(item => {
      // Filter by search term
      if (props.filterTerm) {
        const matchesSearch =
          item.documentNameExternal.toLowerCase().includes(lowerFilter) ||
          item.documentNameInternal.toLowerCase().includes(lowerFilter) ||
          item.residentGroups.toLowerCase().includes(lowerFilter) ||
          item.buildingsLabel.toLowerCase().includes(lowerFilter)
        if (!matchesSearch) {
          return false
        }
      }
      // Filter by selected tags (if array exists, even empty means "filter by none" = show nothing)
      if (props.selectedTagFilters.length === 0) {
        return true // No tags selected = show all documents
      }
      return item.tags.some(tag => props.selectedTagFilters.includes(tag))
    })

  // sort buildings so that matches appear first
  dataToRender.forEach(item => {
    if (!props.filterTerm) {
      return
    }
    item.buildings.sort((a, b) =>
      a.displayName.toLowerCase().includes(lowerFilter) ? -1 : b.displayName.toLowerCase().includes(lowerFilter) ? 1 : 0,
    )
  })

  const columns = [
    new ColumnConfig({
      title: 'Document name (Dashboard)',
      renderPrimitive: item => item.documentNameInternal,
      render: (r, col) => {
        return (
          <div>
            <HighlightText search={props.filterTerm}>{col.renderPrimitive(r)}</HighlightText>
          </div>
        )
      },
      comparator: ColumnConfig.simpleComparatorGenerator('documentNameInternal'),
    }),

    new ColumnConfig({
      title: 'Document name (App)',
      renderPrimitive: item => item.documentNameExternal,
      render: (r, col) => {
        return (
          <div>
            <HighlightText search={props.filterTerm}>{col.renderPrimitive(r)}</HighlightText>
          </div>
        )
      },
      comparator: ColumnConfig.simpleComparatorGenerator('documentNameExternal'),
    }),

    new ColumnConfig({
      title: 'Resident group(s)',
      renderPrimitive: item => item.residentGroups,
      render: (r, col) => {
        return (
          <div>
            <HighlightText search={props.filterTerm}>{col.renderPrimitive(r)}</HighlightText>
          </div>
        )
      },
      comparator: ColumnConfig.simpleComparatorGenerator('residentGroups'),
    }),

    getTagLabelsColumnConfig(),

    getBuildingLabelsColumnConfig(props.filterTerm),
  ]

  if (dataToRender.length > 0) {
    return (
      <SortableTable
        data={dataToRender}
        columns={columns}
        getKey={d => d.documentId}
        onClickRow={r => props.onClickDocument(r.documentId)}
      />
    )
  }

  // Show appropriate empty state
  if (hasFiltersApplied && allDocumentsInOrg.length > 0) {
    return <EmptyStateIconAndMessage message={'No documents match the selected filter(s)'} icon={RXRIcon.APP_DOCUMENTS} />
  }

  return <EmptyStateIconAndMessage message={'No documents in org'} icon={RXRIcon.APP_DOCUMENTS} />
}

ListDocumentsTable.propTypes = {
  filterTerm: PropTypes.string,
  onClickDocument: PropTypes.func,
  selectedTagFilters: PropTypes.arrayOf(PropTypes.string),
}

export default ListDocumentsTable
